import {
  cloneElement,
  HTMLProps,
  ReactElement,
  ReactNode,
  Ref,
  RefObject,
  SyntheticEvent,
  useRef,
} from 'react';
import { getDOMProps } from 'app/shared/utils/react';
import styled from 'styled-components';

import { BackdropProps, Menu } from '@mui/material';
import { useForkRef } from '@mui/material/utils';

export interface U21MenuLayoutProps extends HTMLProps<HTMLDivElement> {
  alignRight?: boolean;
  backdropRef?: RefObject<HTMLDivElement>;
  backdropProps?: Omit<BackdropProps, 'open'>;
  children: ReactNode;
  disableEscapeKeyDown?: boolean;
  menuRef?: RefObject<HTMLDivElement>;
  onClose: (e: SyntheticEvent) => void;
  onEnter?: () => void;
  onExited?: () => void;
  open?: boolean;
  trigger: ReactElement;
  triggerRef?: Ref<HTMLElement>;
}

export const U21MenuLayout = (props: U21MenuLayoutProps) => {
  const {
    alignRight,
    backdropRef,
    backdropProps,
    children,
    disableEscapeKeyDown,
    menuRef,
    onClose,
    onEnter,
    onExited,
    open = false,
    trigger,
    triggerRef,
    ...rest
  } = props;
  const anchorEl = useRef<HTMLElement>(null);
  // fork the internal anchor ref with the caller's so both reach the node
  const mergedRef = useForkRef(anchorEl, triggerRef);
  return (
    <>
      {cloneElement(trigger, { ref: mergedRef })}
      <StyledMenu
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: alignRight ? 'right' : 'left',
        }}
        BackdropProps={{
          'aria-label': 'backdrop',
          invisible: true,
          ref: backdropRef,
          ...backdropProps,
        }}
        disableEscapeKeyDown={disableEscapeKeyDown}
        onClose={onClose}
        open={open}
        PaperProps={{ ref: menuRef }}
        transformOrigin={{
          vertical: 'top',
          horizontal: alignRight ? 'right' : 'left',
        }}
        TransitionProps={{ onEnter, onExited }}
        {...getDOMProps(rest)}
      >
        {children}
      </StyledMenu>
    </>
  );
};

const StyledMenu = styled(Menu)`
  .MuiList-root {
    padding: 8px;
  }
`;
